// 工具列表页面
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {debounce} from "lodash-es";
import {history} from 'umi';
import {useRequest} from "ahooks";
import moment from "moment";

import {
  Layout,
  SearchBox,
  Button,
  Justify,
  Tabs,
  Modal,
  Switch,
  Bubble,
  notification,
  StatusTip
} from "tea-component";
import DOMPurify from "dompurify";
import SvgIcon from "@/pages/home/components/SvgIcon";
import MultipleSegment from "@/pages/aigc/components/MultipleSegment";
import ToolCard from "@/pages/aigc/components/AigcCard";
import EmptyStatus from "@/pages/aigc/components/EmptyStatus";
import VirtualizedList from "@/pages/aigc/components/VirtualizedList";
import ToolCardDetail from "@/pages/aigc/pages/Tools/components/ToolCardDetail";
import {deleteTool, getTagList, getToolList} from "@/pages/aigc/pages/Tools/service";
import {unitConversion} from "@/pages/datalab/utils/utils";
import {buriedPointPage} from "@/pages/home/utils/buriedPoint";
import {useAigcIntl} from "@/pages/aigc/utils/utils";
import "./index.scss";


const {Content} = Layout;

enum TagType {
  Class = 'class', // 类型
  Scene = 'scene', // 场景
}

enum CardType {
  All = "",
  Tool = "tool", // 工具
  Example = 'example', // 案例
}


export default function Tools() {

  useEffect(() => {
    buriedPointPage({
      pageId: '700101',
      pageName: 'AITools',
      extInfo: '',
      pagePath: '/AITools',
      typeId: "aigc",
    });
  }, []);

  const {formatMessage: toolsFormatMessage, commonFormatMessage, isEn} = useAigcIntl("tools");

  // 搜索
  const [searchValue, setSearchValue] = useState<string>("");
  // 用户当前选择的标签类型
  const [currentCardType, setCurrentCardType] = useState<string>(CardType.All);
  // 用户选择的标签
  const [classTagValue, setClassTagValue] = useState([]);
  const [sceneTagValue, setSceneTagValue] = useState([]);
  // 是否仅显示内部可用
  const [isInner, setIsInner] = useState(false);

  // 后端返回的标签列表数据
  const [tagData, setTagData] = useState([]);
  // 后端返回的 工具列表数据
  const [toolData, setToolData] = useState([]);
  // page 是请求工具列表数据的偏移量; total是代表工具的总数量; pageSize是每次请求的条数
  const toolInfo = useRef({total: 0, page: 0, pageSize: 50}).current;

  const [toolDetailId, setToolDetailId] = useState(null);

  // 保存当前每行有多少个卡片, 用于虚拟滚动渲染
  const [count, setCount] = useState(4); // 最少四个

  // 滚动虚拟动态加载
  const containerRef = useRef(null);
  useLayoutEffect(() => { // 计算每行有多少个卡片, 用于虚拟滚动时的计算

    // 不使用resize事件是因为, 如果在每行5个卡片的情况下刷新页面, 由于没有触发resize事件, 导致count为4, 在计算虚拟滚动起始位置时出现错误.
    const resizeObserver = new ResizeObserver(debounce(() => {
      if (window.innerWidth < 1750) {
        setCount(4);
      } else if (window.innerWidth < 2100) {
        setCount(5);
      } else if (window.innerWidth < 2410) {
        setCount(6);
      } else {
        setCount(7);
      }
    }, 300));
    resizeObserver.observe(containerRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // 请求标签列表数据 (应后端要求传递1000 和 0)
  const {
    loading: tagListLoading,
    error: tagListError
  } = useRequest(
    () => getTagList({pagesize: 1000, offeset: 0}),
    {
      onSuccess(data) {
        setTagData(data);
        const newClassTagValue: any[] = [];
        const newSceneTagValue: any[] = [];
        data?.forEach((tag) => {
          tag.side_tag_type === TagType.Class ? newClassTagValue.push(tag.id) : newSceneTagValue.push(tag.id);
        });
        setClassTagValue(newClassTagValue);
        setSceneTagValue(newSceneTagValue);
      },
      onError() {
      }
    }
  );

  // 请求工具列表数据
  const {
    loading: toolListLoading,
    error: toolListError,
    run: toolListRun,
  } = useRequest(
    (params, page=0) => {
      (page===0) && setToolData([]);
      toolInfo.page = page;
      const newParams = {
        pagesize: toolInfo.pageSize,
        offset: toolInfo.page * toolInfo.pageSize,
        keyword: searchValue,
        is_inner: isInner,
        scene_tag_ids: sceneTagValue?.join(","),
        class_tag_ids: classTagValue?.join(","),
        ai_tool_type: currentCardType,
        ...params
      };
      return getToolList(newParams);
    },
    {
      // manual: true,
      onSuccess(data) {
        let list = data?.list || [];

        if (toolInfo.page > 0) {
          // 如果当前请求工具列表的偏移量不是0, 那么数据需要合并, 而不是替换.
          list = toolData.concat(list);
        }
        toolInfo.total = data?.total || 0;
        setToolData(list);
      },
      onError() {
      }
    }
  );

  // 用于搜索时请求的防抖
  const debounceToolListRun = useCallback(debounce((keyword) => {
    toolInfo.page = 0;
    toolListRun({keyword});
  }, 500), []);

  // 标签列表数据
  const tagGroup = useMemo(() => {
    if (!tagData) return [];
    const tagGroup: any[] = [{
      name: toolsFormatMessage(TagType.Class),
      options: [],
      type: TagType.Class
    }, {name: toolsFormatMessage(TagType.Scene), options: [], type: TagType.Scene}];
    tagData?.forEach((tag: any) => {
      let index = 0;
      (tag.side_tag_type === TagType.Scene) && (index = 1);
      tagGroup[index].options.push({value: tag.id, text: isEn ? tag.tag_name_en : tag.tag_name});
    });
    return tagGroup;

  }, [isEn, tagData]);


  /**
	 * 工具列表加载的几种情况
	 * 1. 页面初始化加载
	 * 2. 搜索加载 (需要节流)
	 * 3. 标签change后加载
	 * 4. 滚动加载
	 * (前端渲染需要滚动虚拟加载)
	 */
  // 搜索处理
  const handleSearchTools = (value) => {
    setSearchValue(value.trim());
    // 防抖请求工具列表
    debounceToolListRun(value.trim());
  };

  // 卡片删除操作
  const deleteCard = (event, {id, name}) => {
    event.stopPropagation();
    Modal.confirm({
      message: toolsFormatMessage("delete_message"),
      description: <span dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(toolsFormatMessage("delete_des", {name}))}}/>,
      okText: commonFormatMessage("confirm"),
      onOk() {
        deleteTool({id}).then(() => {
          notification.success({description: commonFormatMessage("delete_success")});
          // 刷新页面
          setToolData(toolData?.filter(data => data?.id !== id));
        }).catch((e) => {
          console.error("删除工具错误: ", e);
          notification.error({description: commonFormatMessage("error_info", {code: e?.code || ""})});
        });
      }
    });
  };

  // 卡片编辑操作
  const editCard = (event, tool) => {
    event.stopPropagation();
    history.push("ResourceUpload", JSON.stringify(tool || {}));
  };

  const tabs = [
    {id: CardType.All, label: (<><SvgIcon type={'aigc-main'}/>{commonFormatMessage("all")}</>)},
    {id: CardType.Tool, label: (<><SvgIcon type={'aigc-tool'}/>{toolsFormatMessage(CardType.Tool)}</>)},
    {id: CardType.Example, label: (<><SvgIcon type={'aigc-example'}/>{toolsFormatMessage(CardType.Example)}</>)},
  ];

  return (
    <Content className={"aigc-tools"}>
      <Content.Header
        title={toolsFormatMessage("title")}
        className={"tools-header"}
      >
        {/* 搜索框, 聊天按钮*/}
        <br/>
        <div
          className={"tools-search"}
        >
          <SvgIcon type={"search"}/>
          <SearchBox
            placeholder={toolsFormatMessage("search_placeholder")}
            value={searchValue}
            onChange={handleSearchTools}
            onSearch={handleSearchTools}
            onClear={() => {
              setSearchValue("");
              handleSearchTools("");
            }}
          />
        </div>
        <Button className={"search-btn"} type={"primary"} onClick={() => {
          if (!searchValue) return;
          toolInfo.page = 0;
          toolListRun({keyword: searchValue});
        }}>{commonFormatMessage("search")}</Button>
        <Button
          className={"search-btn chat-btn"}
          onClick={() => {
            window.open(`${window.location.origin  }/v2/chatgpt`);
          }}
        >{toolsFormatMessage("chat")}</Button>

        {/* 头部左侧的分类切换 和 右侧的 资源上传按钮 */}
        <Justify
          left={(<Tabs className={"tool-tag-tab"} tabs={tabs} activeId={currentCardType} onActive={(tab) => {
            setCurrentCardType(tab.id);
            toolInfo.page = 0;
            toolListRun({ai_tool_type: tab.id});
          }}/>)}
          right={(<Button className={"tool-upload"} onClick={() => {
            history.push(`/aigc/ResourceUpload`);
            // 跳转到在线文档
            // window.open("https://doc.weixin.qq.com/forms/AJEAIQdfAAoASMAUwb5AMIGIC1Zp10Sdf?journal_source=chat&version=4.1.6.6017&platform=win#/fill");
          }}><SvgIcon type={"aigc-joint"}/>{toolsFormatMessage("resource_upload")}</Button>)}
        />
      </Content.Header>
      <Content.Body full className={"tools-body"} ref={containerRef}>
        {/* 标签tag 多选, */}
        <MultipleSegment.Group
          className={"tools-tags"}
          loading={tagListLoading}
          error={tagListError}
          hiddenComponent={(
            <div className={"tools-tag-other"}><span className={"name"}>{commonFormatMessage("other")}</span> <Switch
              onChange={(value) => {
                setIsInner(value);
                toolInfo.page = 0;
                toolListRun({is_inner: value});
              }}>{toolsFormatMessage("inner_info")}</Switch></div>)}
        >
          <MultipleSegment key={TagType.Class} name={toolsFormatMessage(TagType.Class)} options={tagGroup[0].options}
					                 value={classTagValue} onChange={(value) => {
              setClassTagValue(value);
              toolInfo.page = 0;
              toolListRun({class_tag_ids: value?.join(",")});
            }}/>
          <MultipleSegment key={TagType.Scene} name={toolsFormatMessage(TagType.Scene)} options={tagGroup[1].options}
					                 value={sceneTagValue} onChange={(value) => {
              setSceneTagValue(value);
              toolInfo.page = 0;
              toolListRun({scene_tag_ids: value?.join(",")});
            }}/>
        </MultipleSegment.Group>
        <VirtualizedList
          container={containerRef.current} // 绑定滚动事件的容器元素, 可以不传
          // containerHeight={200} // 不传容器元素时, 最好给默认的容器元素一个固定高度
          rowHeight={355} // 滚动过程中, 每行元素的高度
          rowSize={4} // 希望渲染加载几行的元素
          colSize={count} // 每行有几列, 可以不传, 默认为1
          source={toolData} // 数据源
          // eslint-disable-next-line @typescript-eslint/naming-convention
          renderItem={(tool: any, _index: number) => {
            /* eslint-disable @typescript-eslint/naming-convention */
            const {
              id,
              cover,
              name_en,
              name,
              create_time,
              description,
              description_en,
              tag_names,
              tag_names_en,
              user_name,
              view_count,
              ai_tool_type,
              is_edit_able,
              is_delete_able,
              is_inner_show
            } = tool || {};
            /* eslint-enable @typescript-eslint/naming-convention */
            const tagStr = isEn ? tag_names_en : tag_names;
            const isNew = moment().diff(moment(create_time), "hour") <= 24;
            return (
              <ToolCard
                className={"tool-item"}
                key={id}
                coverContent={(
                  <div className={`tool-cover ${is_edit_able || is_delete_able ? "tool-cover-update" : ""}`}>
                    <div>
                      {is_edit_able ? <Bubble
                        dark
                        content={commonFormatMessage("edit")}
                      >
                        <Button
                          icon="pencil"
                          className="ns-btn--icon"
                          onClick={(e) => editCard(e, tool)}
                        />
                      </Bubble> : ""}
                      {is_delete_able ? <Bubble
                        dark
                        content={commonFormatMessage("delete")}
                      >
                        <Button
                          icon="delete"
                          className="ns-btn--icon"
                          onClick={(event) => deleteCard(event, {id, name: isEn ? name_en : name,})}
                        />
                      </Bubble> : ""}
                    </div>
                    <div>
                      {is_inner_show ? <span className={"new-tag"}>{toolsFormatMessage("tag_internal")}</span> : ""}
                      {isNew ? <span className={"new-tag"}>{toolsFormatMessage("tag_new")}</span> : ""}
                    </div>
                  </div>)}
                coverImgSrc={cover}
                title={{
                  icon: (<SvgIcon className={"card-title-icon"} type={`aigc-${  ai_tool_type}`}/>),
                  text: isEn ? name_en : name
                }}
                subTitle={{
                  left: user_name,
                  right: moment(create_time).format("YYYY-MM-DD")
                }}
                content={isEn ? description_en : description}
                footer={{
                  left: tagStr?.split(",")?.slice(0, 2)?.map((str: string, index: number) => str && (
                    <span key={str + index} className={"tool-tag"}>{str}</span>)),
                  right: (
                    <span className={"tool-views"}><SvgIcon type={"open-eyes"}/>{unitConversion(view_count)}</span>)
                }}
                onClick={() => {
                  setToolDetailId(id);
                }}
              />
            );
          }} // 渲染数据的函数
          loading={toolListLoading} // 加载状态
          error={toolListError} // 错误状态
          loadComponent={(<div className={"loader-center"}><StatusTip.LoadingTip /></div>)} // 滚动加载时显示的加载组件
          errorComponent={(<div className={"loader-center"}><StatusTip.ErrorTip onRetry={() => toolListRun({}, toolInfo.page)} /></div>)} // 滚动加载时 错误状态组件
          emptyComponent={<EmptyStatus info={toolsFormatMessage("empty")} height={"400px"}/>} // 如果source为空时, 显示的空态
          onScrollBottom={() => {
            if ((toolData?.length < toolInfo.total) && (!toolListLoading) && (!toolListError)) {
              toolInfo.page += 1;
              toolListRun({}, toolInfo.page);
            }
          }} // 滚动到底部时的处理函数
        />
        <Modal
          className={"aigc-tool-detail-modal"}
          visible={toolDetailId !== null}
          disableCloseIcon
          size={"auto"}
        >
          <Modal.Body>
            <ToolCardDetail
              isEn={isEn}
              toolId={toolDetailId}
              changeToolDetailId={(id) => setToolDetailId(id)}
            />
          </Modal.Body>
        </Modal>
      </Content.Body>
    </Content>
  );
}
